// Embed protocol — the message contract between this Maddox host and an embedded
// sub-app (the Quality Agent today; more to come). Kept small and self-contained
// so it can later be lifted into a shared `@maddox/embed-sdk` package; the
// sub-app defines a byte-for-byte matching copy of these types.
//
// Every message is wrapped in an envelope with a discriminator (`__maddoxEmbed`)
// so unrelated postMessage traffic is ignored, and a version so the contract can
// evolve without silently breaking either side.

export const EMBED_PROTOCOL_VERSION = 1;

export type EmbedMessageType =
  // child → host
  | 'ready'
  | 'token:refresh'
  | 'layout'
  // host → child
  | 'init'
  | 'context'
  | 'token'
  | 'teardown';

export interface EmbedEnvelope<T = unknown> {
  __maddoxEmbed: true;
  v: number;
  type: EmbedMessageType;
  reqId?: string;
  payload: T;
}

// Non-secret context the host pushes to the sub-app. For the Quality Agent this
// selects which customer/project is in view.
export type EmbedPanelLayout = 'floating' | 'sidebar' | 'fullscreen';

export interface EmbedContext {
  customer: string;
  projectId: string;
  modelId?: string;
  // The defect class the sub-app files its results under (Defect Studio: every accepted variant
  // is uploaded to `modelId` as this label). Optional and additive, like `language`.
  labelId?: string;
  // How the host names that class to the user (its alias), so the sub-app shows the same name —
  // the id prefix goes stale once a class is renamed.
  labelName?: string;
  // Set when the host shows the sub-app inside its agent panel. The sub-app then
  // drops its own page chrome (the host renders the header) and adapts to the
  // panel size; absent for the full-page embed.
  panelLayout?: EmbedPanelLayout;
  // The UI language the user selected in this host (Tolgee tag, e.g. "en" /
  // "de-DE"), so the sub-app renders its own chrome in the same language.
  // Optional and additive: a sub-app that predates it just ignores it, and a
  // host that omits it leaves the sub-app on its English default.
  language?: string;
}

export interface ReadyPayload {
  appId: string;
  protocolVersions: number[];
}

export interface InitPayload {
  context: EmbedContext;
  token: string;
  expiresAt: string;
  // Optional protocol features this host supports beyond the v1 baseline
  // ('layout': it obeys `layout` messages). Additive — the child hides the
  // corresponding UI when a capability is absent, so a newer child against an
  // older host degrades to the baseline instead of showing dead controls.
  capabilities?: string[];
}

// child → host: how the host should lay out the iframe. `fullscreen` expands it
// to cover the whole host viewport (an in-app overlay, not browser fullscreen).
export interface LayoutPayload {
  fullscreen: boolean;
}

export interface TokenPayload {
  token: string;
  expiresAt: string;
}

export function isEmbedEnvelope(data: unknown): data is EmbedEnvelope {
  return typeof data === 'object' && data !== null && (data as {__maddoxEmbed?: unknown}).__maddoxEmbed === true;
}
